import { DateTime } from 'luxon'
import { BillingCategoryCode } from './Receipt'

///////////////////////////////////
// Types
///////////////////////////////////

const DiscountPatternCode = {
  Fixed: '6', // 固定割引
  AllElectric: '7', // オール電化割引
} as const
type DiscountPatternCode = (typeof DiscountPatternCode)[keyof typeof DiscountPatternCode]

export const DunningPatternCode = {
  None: '00',
  Pattern1: '01',
  Pattern2: '02',
  Pattern3: '03',
  Pattern4: '04',
} as const
export type DunningPatternCode = (typeof DunningPatternCode)[keyof typeof DunningPatternCode]

///////////////////////////////////
// InvoiceBuilding
///////////////////////////////////

export interface InvoiceBuildingData {
  name: string
  address: string
  roomName: string
}

export class InvoiceBuilding {
  declare name: string
  declare address: string
  declare roomName: string

  constructor(params: InvoiceBuildingData) {
    Object.assign(this, params)
  }
}

///////////////////////////////////
// Detail
///////////////////////////////////

export interface DetailData {
  title: string
  consumption: number
  unitPrice: number
  price: number
}

export class Detail {
  declare title: string
  declare consumption: number
  declare unitPrice: number
  declare price: number

  constructor(params: DetailData) {
    Object.assign(this, params)
  }
}

///////////////////////////////////
// ReadingValue
///////////////////////////////////

export interface ReadingValueData {
  date: string
  meterNumber: string | null
  preValue: number
  value: number
}

export class ReadingValue {
  declare date: DateTime
  declare meterNumber: string | null
  declare preValue: number
  declare value: number

  constructor(params: ReadingValueData) {
    Object.assign(this, params)
    this.date = DateTime.fromISO(params.date)
  }
}

///////////////////////////////////
// Invoice
///////////////////////////////////

export type InvoiceData = {
  dailyRateDays: number
  consumption: number
  fuelAdjustmentPrice: number
  solarPowerPrice: number
  renewableEnergyPrice: number
  basicPrice: number
  subtotalAmount: number
  discountAmount: number
  fixedDiscountAmount: number
  tsunaguNetDiscountAmount: number
  paperlessDiscountAmount: number
  accountTransferDiscountAmount: number
  adjustmentPrice: number
  solarPowerReturnFee: number
  appropriationAmount: number
  preBillingAmount: number
  billingAmount: number
  consumptionTaxPrice: number
  evPenaltyAmount: number
  dunningDelayAmount: number
  adjustmentPriceLabel: string | null
  remarks: string | null
  userCode: string | null
  userName: string | null
  basicCapacity: string
  roomType: string
  billingCategoryCode: BillingCategoryCode

  building: InvoiceBuildingData
  groupName: string | null
  publishedAt: string | null
  basicCapacityUnit: string | null
  previousMeterReadOn: string
  meterReadOn: string
  date: string
  dunningPatternCode: DunningPatternCode
  readingValues: ReadingValueData[]
  previousConsumption: number | null
  payerName: string
  userHonorific: string
  details: DetailData[]
  consumptionTax: number
  renewableEnergyConsumption: number
  renewableEnergyUnitPrice: number
  minimumRenewableEnergy: number
  fuelAdjustmentConsumption: number
  fuelAdjustmentUnitPrice: number
  minimumFuelAdjustment: number
  minimumFeeSystemConsumption: number
  discountPatternCode: string
  transferDate: string | null
}

export default class Invoice {
  declare dailyRateDays: number
  declare consumption: number
  declare fuelAdjustmentPrice: number
  declare solarPowerPrice: number
  declare renewableEnergyPrice: number
  declare basicPrice: number
  declare subtotalAmount: number
  declare discountAmount: number
  declare fixedDiscountAmount: number
  declare tsunaguNetDiscountAmount: number
  declare paperlessDiscountAmount: number
  declare accountTransferDiscountAmount: number
  declare adjustmentPrice: number
  declare solarPowerReturnFee: number
  declare appropriationAmount: number
  declare preBillingAmount: number
  declare billingAmount: number
  declare consumptionTaxPrice: number
  declare evPenaltyAmount: number
  declare dunningDelayAmount: number
  declare adjustmentPriceLabel: string | null
  declare remarks: string | null
  declare userCode: string | null
  declare userName: string | null
  declare basicCapacity: string
  declare roomType: string
  declare billingCategoryCode: BillingCategoryCode

  declare building: InvoiceBuilding

  declare groupName: string | null
  declare publishedAt: DateTime | null
  declare basicCapacityUnit: string | null
  declare previousMeterReadOn: DateTime
  declare meterReadOn: DateTime
  declare date: DateTime
  declare dunningPatternCode: DunningPatternCode
  declare readingValues: ReadingValue[]
  declare previousConsumption: number | null
  declare payerName: string
  declare userHonorific: string
  declare details: Detail[]
  declare consumptionTax: number
  declare renewableEnergyConsumption: number
  declare renewableEnergyUnitPrice: number
  declare minimumRenewableEnergy: number
  declare fuelAdjustmentConsumption: number
  declare fuelAdjustmentUnitPrice: number
  declare minimumFuelAdjustment: number
  declare minimumFeeSystemConsumption: number
  declare discountPatternCode: string
  declare transferDate: DateTime | null

  // Computed properties --------------------------------------------------------------

  get fixedDiscountAmountLabel() {
    switch (this.discountPatternCode) {
      case DiscountPatternCode.Fixed:
        return '固定割引'
      case DiscountPatternCode.AllElectric:
        return 'オール電化割引'
      default:
        return '固定割引'
    }
  }

  get numberOfDiscountField() {
    let count = 0
    if (this.discountAmount > 0) count++
    if (this.fixedDiscountAmount > 0) count++
    if (this.tsunaguNetDiscountAmount > 0) count++
    if (this.paperlessDiscountAmount > 0) count++
    if (this.accountTransferDiscountAmount > 0) count++
    if (this.solarPowerReturnFee > 0) count++
    if (this.solarPowerReturnFee > 0) count++
    if (this.preBillingAmount !== 0) count++
    if (this.adjustmentPrice !== 0) count++
    if (this.evPenaltyAmount > 0) count++
    if (this.dunningDelayAmount > 0) count++
    return count
  }

  get userCodeForDisplay() {
    return this.userCode?.slice(0, 8)
  }

  get billingTypeText() {
    switch (this.billingCategoryCode) {
      case BillingCategoryCode.AccountTransfer: {
        const date = this.transferDate?.toFormat('M月d日') ?? '(未定)'
        return `ご指定の口座より ${date} に振替させて頂きます。`
      }
      case BillingCategoryCode.CreditCard:
        return 'ご指定のクレジット会社よりご請求させて頂きます。'
      case BillingCategoryCode.ConvenienceStore:
        return 'コンビニエンスストアでお支払いください。（後日郵送）'
      default:
        return ''
    }
  }

  get totalAmount() {
    if (this.dunningPatternCode === DunningPatternCode.Pattern4)
      return this.billingAmount + this.evPenaltyAmount + this.dunningDelayAmount
    return this.billingAmount + this.evPenaltyAmount
  }

  get totalConsumptionTaxPrice() {
    return Math.floor((this.totalAmount / (1 + this.consumptionTax)) * this.consumptionTax)
  }

  // Constructor -----------------------------------------------------------------------

  constructor(params: InvoiceData) {
    Object.assign(this, params)

    this.publishedAt = params.publishedAt ? DateTime.fromISO(params.publishedAt) : null
    this.previousMeterReadOn = DateTime.fromISO(params.previousMeterReadOn)
    this.meterReadOn = DateTime.fromISO(params.meterReadOn)
    this.date = DateTime.fromISO(params.date)
    this.transferDate = params.transferDate ? DateTime.fromISO(params.transferDate) : null

    this.building = new InvoiceBuilding(params.building)
    this.readingValues = params.readingValues.map((readingValue) => new ReadingValue(readingValue))
    this.details = params.details.map((detail) => new Detail(detail))
  }
}
